/**
 * Clean facade for maintenance history operations
 * Single responsibility: read/write maintenance_history via useStorageState
 */

import React, { useCallback } from 'react';
import { devLog } from '../../../utils/devLog';

import { useStorageState } from '../../useStorageState';
import { filterTombstoned } from '../../../sync/tombstone';  // FT-458: hide soft-deleted maintenance history records
import { normalizeHistory, getDefaultPerformer } from './utils';
import { useVessel } from '../../../contexts/VesselContext';

const makeId = () => (crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`);
const norm = v => (v ?? '').toString().trim();

export function useMaintenanceHistory() {
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();
  const { value: raw = [], setValue } = useStorageState('maintenance_history', []);
  const { value: currentUser } = useStorageState('current_user', null, { noScope: true });
  const migrationRanRef = React.useRef(false);

  // 🚨 BUG 2 FIX: Lightweight migration for legacy records missing jobId
  // CRITICAL DISCOVERY: maintenance_history contains BOTH job definitions AND completion records
  // Only migrate COMPLETION records - skip job DEFINITIONS (templates)
  React.useEffect(() => {
    // Only run once, and only if we have data
    if (migrationRanRef.current || !raw || raw.length === 0) return;

    const normalized = normalizeHistory(raw);

    // Separate completion records from job definitions
    // Completion: has completedAt/performedBy/status='completed'
    // Definition: has interval/frequency/defaultPurpose/linkedEquipmentIds
    const completions = normalized.filter(r =>
      r.type === 'safety_check' || r.completedAt || r.performedBy || r.status === 'completed'
    );
    const definitions = normalized.filter(r =>
      r.interval || r.frequency || r.defaultPurpose || r.linkedEquipmentIds?.length > 0
    );

    console.log(`[useMaintenanceHistory] 📊 Found ${completions.length} completion records, ${definitions.length} job definitions`);

    const needsMigration = completions.some(r => !r.jobId);

    if (needsMigration || definitions.length > 0) {
      migrationRanRef.current = true;
      console.log('[useMaintenanceHistory] 🔧 BUG 2 MIGRATION: Processing records (skipping job definitions)');

      const migrated = normalized.map(record => {
        // SKIP job definitions - they're architectural pollution, don't need jobId
        if (record.interval || record.frequency || record.defaultPurpose) {
          return record;  // Return unchanged
        }

        // FT-267: ad-hoc records intentionally have jobId: null — do not synthesise a legacy jobId
        if ((record.completionType || 'routine') === 'adhoc') {
          return record;
        }

        // Skip completions that already have jobId
        if (record.jobId) return record;

        // Completion record missing jobId - attempt to infer
        let inferredJobId = null;

        if (record.equipmentType && record.equipmentType !== 'unknown') {
          inferredJobId = `legacy.${record.equipmentType.toLowerCase().replace(/\s+/g, '_')}`;
        } else if (record.item) {
          inferredJobId = `legacy.${record.item.toLowerCase().replace(/\s+/g, '_')}`;
        }

        if (inferredJobId) {
          console.log(`[useMaintenanceHistory] 🔧 Backfilled jobId for ${record.id}: ${inferredJobId}`);
          return { ...record, jobId: inferredJobId, _migratedJobId: true };
        } else {
          console.warn(`[useMaintenanceHistory] ⚠️ Cannot infer jobId for ${record.id}`);
          return { ...record, _legacyNoJobId: true };
        }
      });

      const backfilled = migrated.filter(r => r._migratedJobId).length;
      console.log(`[useMaintenanceHistory] ✅ BUG 2 MIGRATION COMPLETE: ${backfilled} backfilled, ${definitions.length} definitions skipped`);

      if (backfilled > 0) {
        setValue(migrated);
      }
    }
  }, [raw, setValue]);

  // Always return normalized array.
  // FT-458: exclude soft-deleted (tombstoned) completion records from every
  // derived read/getter. maintenance_history has NO delete UI today (so no
  // tombstones exist yet), but it is in FT-458 scope — this makes the read
  // filter present + consistent so a future delete path is safe by construction.
  // Writes use setValue(prev=>...) on the raw storage array, preserving tombstones.
  const history = filterTombstoned(normalizeHistory(raw));

  // FT-221: Accept performerName alongside performer (crew ID)
  // FT-267 §15: Accept equipmentNames alongside equipmentIds (FT-221 pattern for equipment)
  // FT-353e: Accept optional `job` so we can inherit `job.originatingDefectId`
  //          into `completion.linkedDefectId` when the caller doesn't pass one
  //          explicitly. Corrective-job completions should auto-link back to
  //          the defect that spawned them.
  const addCompletedCheck = ({ fields, performer, performerName, linkedDefectId, equipmentIds, equipmentNames, job }) => {
    const now = Date.now();
    const completionType = fields?.completionType || 'routine';

    // FT-353e: completion inheritance.
    // Precedence:
    //   1. Explicit caller-supplied `linkedDefectId` always wins (allows override
    //      and preserves the FT-267 defect-created-during-completion path where
    //      completeMaintenanceJob creates a fresh defect and links it).
    //   2. Otherwise, if the completion is for a job that carries
    //      `originatingDefectId` (i.e. a corrective job), inherit it so the
    //      history record links back to the originating defect.
    //   3. Otherwise, undefined (existing behaviour).
    // Mismatch (both provided, different values) is logged as a dev warning so
    // callers passing conflicting data get flagged, but explicit still wins.
    let resolvedLinkedDefectId = linkedDefectId;
    const originatingDefectId = job?.originatingDefectId || null;
    if (linkedDefectId && originatingDefectId && linkedDefectId !== originatingDefectId) {
      devLog('[useMaintenanceHistory] FT-353e: linkedDefectId mismatch — explicit wins', {
        explicit: linkedDefectId,
        originating: originatingDefectId,
        jobId: fields?.jobId
      });
    }
    if (!resolvedLinkedDefectId && originatingDefectId) {
      resolvedLinkedDefectId = originatingDefectId;
      devLog('[useMaintenanceHistory] FT-353e: inherited linkedDefectId from job.originatingDefectId', {
        linkedDefectId: resolvedLinkedDefectId,
        jobId: fields?.jobId
      });
    }

    // 🚨 BUG 2 FIX: Defensive check for jobId
    // FT-267: ad-hoc completions legitimately have no jobId — don't trip the guard
    if (!fields?.jobId && completionType !== 'adhoc') {
      console.error('[useMaintenanceHistory] ❌ BUG 2: jobId missing!', {
        title: fields?.title,
        equipmentType: fields?.equipmentType,
        hasFields: !!fields,
        fieldKeys: fields ? Object.keys(fields) : []
      });
    }

    // FT-349a: allow caller to supply completionId so the history record and
    // the job.completions[] entry share the same stable id. Falls back to a
    // fresh id for legacy callers that don't pass one.
    const completionId = fields?.completionId || makeId();

    const item = {
      id: completionId,
      type: 'safety_check',
      status: 'completed',
      completedAt: now,  // Canonical: epoch ms timestamp

      // FT-446 (2026-08-15) SAFETY-CRITICAL: stamp a sync timestamp AT CREATION.
      // The sync push/pull merge tie-break keys off `updatedAt`/`lastSaved`. A
      // completion created WITHOUT one used to coerce to ts=0 and, on the buggy
      // push path, ALWAYS win — so a stale unsigned completion (ts=0) on one
      // device would overwrite the SIGNED copy pushed from another device and
      // silently revert the sign-off to pending. Giving every completion a real
      // creation timestamp closes that: a genuinely newer edit (later
      // updatedAt) still wins; a stale copy no longer clobbers a newer cloud.
      updatedAt: new Date(now).toISOString(),
      lastSaved: new Date(now).toISOString(),

      // FT-394: sign-off state machine — all new completions start as 'pending'
      signedOffStatus: 'pending',   // 'pending' | 'signed_off'
      signedOffBy: null,
      signedOffAt: null,
      signedOffRole: null,

      // FT-349a: photos move from job.photos → completion.photos (per-check
      // evidence). Default to [] when the caller doesn't pass any.
      photos: Array.isArray(fields?.photos) ? fields.photos : [],
      performedBy: performer ?? getDefaultPerformer(currentUser, vessel),  // Canonical: crew ID
      performerName: performerName || null,  // FT-221: Display name for history view
      equipmentType: norm(fields?.equipmentType || 'unknown'),

      // NEW: Direct equipment linking (backwards compatible)
      ...(equipmentIds && equipmentIds.length > 0 && { equipmentIds }),

      // FT-267 §15: stored at write time, FT-221 pattern for equipment
      equipmentNames: equipmentNames || [],

      // Additional fields for compatibility and display
      date: fields?.date || new Date(now).toISOString().split('T')[0],  // For display
      inspector: performer || fields?.inspector,  // Legacy compatibility
      notes: fields?.notes || '',
      defectsFound: fields?.defectsFound || fields?.hasDefects || false,
      hasDefects: fields?.hasDefects || fields?.defectsFound || false,
      defectDetails: fields?.defectDetails || '',
      defectData: fields?.defectData || null,  // Full defect data from modal
      allUnitsChecked: fields?.allUnitsChecked !== false,
      equipmentChecks: fields?.equipmentChecks || {},
      checkType: fields?.checkType,
      title: fields?.title,

      // FT-267: ad-hoc completion fields (additive, default-safe for existing records)
      completionType,
      adhocItem: fields?.adhocItem || null,
      hoursSpent: typeof fields?.hoursSpent === 'number' ? fields.hoursSpent : null,
      category: fields?.category || null,

      // 🚨 BUG 2 FIX: CRITICAL - jobId must exist for UI filtering to work
      // UI uses filterJobHistory(history, equipmentType, jobId) which filters by h.jobId === jobId
      // If jobId is undefined, ALL records are filtered out → "No check history available"
      jobId: fields?.jobId || null,  // Explicit null if missing (for debugging)

      vesselId: vessel?.id || 'default',
      // FT-353e: resolvedLinkedDefectId honours explicit > inherited > none.
      ...(resolvedLinkedDefectId && { linkedDefectId: resolvedLinkedDefectId })
    };
    
    devLog('[useMaintenanceHistory] Adding check:', {
      equipmentType: item.equipmentType,
      equipmentIds: item.equipmentIds,
      defectsFound: item.defectsFound,
      linkedDefectId: item.linkedDefectId,
      jobId: item.jobId  // 🚨 BUG 2 FIX: Log jobId to verify it's being written
    });

    setValue(prev => {
      const list = normalizeHistory(prev);
      devLog('[useMaintenanceHistory] Current history:', list.length, 'items');
      const updated = [...list, item];
      devLog('[useMaintenanceHistory] New history:', updated.length, 'items');
      devLog('[useMaintenanceHistory] 🚨 BUG 2 TRACKING: Writing record with jobId =', item.jobId);
      return updated; // append → always a real change
    });
    
    return item.id;
  };

  // FT-190: DEPRECATED - use getByJobId instead
  // This function matches by equipmentType which causes phantom history
  const getByEquipmentType = (equipmentType) => {
    console.warn('[useMaintenanceHistory] FT-190: getByEquipmentType is DEPRECATED - use getByJobId instead');
    const target = norm(equipmentType).toLowerCase();
    return history.filter(h => {
      const hType = norm(h.equipmentType).toLowerCase();
      return hType === target;
    });
  };

  // FT-190: New safe method that filters by jobId only
  const getByJobId = (jobId) => {
    if (!jobId) return [];
    return history.filter(h => h.jobId === jobId);
  };

  const getHistoryForEquipmentId = (equipmentId) => {
    return history.filter(record => 
      record.equipmentIds && record.equipmentIds.includes(equipmentId)
    );
  };

  // FT-190 FIX: Build map keyed by JOBID (not equipmentType) to prevent phantom history
  // OLD: Used equipmentType as key, causing history from old deleted jobs to appear in new jobs
  // NEW: Use jobId as key - only exact matches count
  const getLastCompleted = () => {
    // Build a map of jobId -> last completion (FT-190 FIX)
    const lastCompletedMap = {};
    history.forEach(item => {
      // FT-190: Use jobId as the key, not equipmentType
      const key = item.jobId;
      if (!key) return; // Skip records without jobId (orphaned legacy records)

      const existing = lastCompletedMap[key];
      // Use completedAt for comparison (canonical), fall back to date
      const itemTime = item.completedAt || (item.date ? new Date(item.date).getTime() : 0);
      const existingTime = existing?.completedAt || (existing?.date ? new Date(existing.date).getTime() : 0);

      if (!existing || itemTime > existingTime) {
        lastCompletedMap[key] = {
          date: item.date || (item.completedAt ? new Date(item.completedAt).toISOString().split('T')[0] : null),
          completedAt: item.completedAt,
          performedBy: item.performedBy || item.inspector,  // Use canonical field first
          inspector: item.inspector || item.performedBy,  // Legacy compatibility
          id: item.id,
          jobId: item.jobId  // FT-190: Include jobId for debugging
        };
      }
    });
    return lastCompletedMap;
  };

  /**
   * Update a completion record with audit trail
   * MCA Compliance: Tracks field-level changes for inspection records
   *
   * @param {string} recordId - The ID of the record to update
   * @param {object} updates - Field updates to apply
   * @param {string} reason - Reason for the edit (required for audit)
   * @returns {boolean} Success status
   */
  const updateCompletionRecord = (recordId, updates, reason) => {
    const userName = currentUser?.name || currentUser?.email || localStorage.getItem('userName') || 'Unknown User';
    const vesselName = vessel?.name || 'Unknown Vessel';

    setValue(prev => {
      const list = normalizeHistory(prev);
      const recordIndex = list.findIndex(r => r.id === recordId);

      if (recordIndex === -1) {
        devLog('[useMaintenanceHistory] Record not found:', recordId);
        return list;
      }

      const existingRecord = list[recordIndex];

      // Track field-level changes for audit
      const fieldsToTrack = [
        'date', 'performedBy', 'notes', 'defectsFound', 'defectDetails',
        'allUnitsChecked', 'checkType', 'title', 'outcome'
      ];

      const changes = [];
      fieldsToTrack.forEach(field => {
        const oldValue = existingRecord[field];
        const newValue = updates[field];
        if (newValue !== undefined && JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push({
            field,
            oldValue: oldValue ?? '',
            newValue: newValue ?? ''
          });
        }
      });

      // Generate summary
      const changedFields = changes.map(c => c.field.replace(/_/g, ' ')).join(', ');
      const summary = changes.length > 0
        ? `Updated ${changedFields}`
        : 'Record updated (no field changes detected)';

      // Create audit entry
      const auditEntry = {
        id: makeId(),
        timestamp: new Date().toISOString(),
        user: userName,
        vessel: vesselName,
        action: 'updated',
        summary,
        changes,
        reason: reason || 'Record updated'
      };

      // Build updated record
      const updatedRecord = {
        ...existingRecord,
        ...updates,
        updatedAt: new Date().toISOString(),
        auditLog: [...(existingRecord.auditLog || []), auditEntry]
      };

      // Replace in list
      const updatedList = [...list];
      updatedList[recordIndex] = updatedRecord;

      devLog('[useMaintenanceHistory] Updated record:', recordId, 'changes:', changes.length);
      return updatedList;
    });

    return true;
  };

  /**
   * Get a single record by ID
   */
  const getRecordById = (recordId) => {
    return history.find(r => r.id === recordId);
  };

  /**
   * FT-394: Sign off a maintenance completion record.
   * Only skipper+ should call this (gate enforced at call site via can('sign', 'maintenance')).
   *
   * @param {string} recordId - The completion record to sign off
   * @returns {boolean} Success
   */
  const signOffCompletion = useCallback((recordId) => {
    const userName = currentUser?.name || currentUser?.email || 'Unknown User';
    const userRole = currentUser?.role || '';
    const now = new Date().toISOString();

    setValue(prev => {
      const list = normalizeHistory(prev);
      const idx = list.findIndex(r => r.id === recordId);
      if (idx === -1) {
        devLog('[useMaintenanceHistory] FT-394: signOffCompletion — record not found:', recordId);
        return prev;
      }
      const updated = [...list];
      updated[idx] = {
        ...updated[idx],
        signedOffStatus: 'signed_off',
        signedOffBy: userName,
        signedOffAt: now,
        signedOffRole: userRole,
        updatedAt: now
      };
      return updated;
    });

    return true;
  }, [setValue, currentUser]);

  return {
    history,
    addCompletedCheck,
    getByEquipmentType,  // FT-190: DEPRECATED - use getByJobId
    getByJobId,          // FT-190: NEW - safe method using strict jobId matching
    getHistoryForEquipmentId,
    getLastCompleted,
    updateCompletionRecord,
    getRecordById,
    signOffCompletion    // FT-394: sign-off state machine
  };
}